import React, { useState, useContext, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  User, 
  Settings, 
  Bell, 
  Shield, 
  Eye, 
  Lock,
  Mail,
  Phone,
  Building,
  MapPin,
  Calendar,
  Edit3,
  Save,
  X,
  CheckCircle,
  AlertTriangle,
  Download,
  Upload,
  Trash2,
  Key,
  Smartphone,
  Monitor,
  Globe,
  Clock,
  BarChart3,
  Award,
  Target,
  TrendingUp,
  FileText,
  Camera
} from 'lucide-react'
import { UserContext } from '../context/UserContext'
import { AssessmentContext } from '../context/AssessmentContext'
import { useUser } from '../context/UserContext'
import { useAuth } from '../context/AuthContext'
import { getUserTypeLabel } from '../utils/userTypeSync'

const Profile = () => {
  const { state: userState, dispatch: userDispatch } = useContext(UserContext)
  const { state: assessmentState } = useContext(AssessmentContext)
  const { canEdit } = useUser()
  const { user, profile } = useAuth()
  const [activeTab, setActiveTab] = useState('profile')
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({
    firstName: profile?.name?.split(' ')[0] || '',
    lastName: profile?.name?.split(' ').slice(1).join(' ') || '',
    email: user?.email || '',
    phone: profile?.phone || '',
    department: profile?.department || '',
    agency: profile?.agency || '',
    position: profile?.position || '',
    location: profile?.location || ''
  })

  // Update editForm when profile changes
  useEffect(() => {
    setEditForm({
      firstName: profile?.name?.split(' ')[0] || '',
      lastName: profile?.name?.split(' ').slice(1).join(' ') || '',
      email: user?.email || '',
      phone: profile?.phone || '',
      department: profile?.department || '',
      agency: profile?.agency || '',
      position: profile?.position || '',
      location: profile?.location || ''
    })
  }, [profile, user])
  const [notifications, setNotifications] = useState({
    emailAlerts: true,
    smsAlerts: false,
    pushNotifications: true,
    weeklyReports: true,
    securityUpdates: true,
    trainingReminders: true,
    incidentAlerts: true
  })
  const [privacy, setPrivacy] = useState({
    profileVisibility: 'agency',
    shareProgress: true,
    allowAnalytics: true,
    dataRetention: '2years'
  })

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'privacy', label: 'Privacy', icon: Eye },
    { id: 'activity', label: 'Activity', icon: BarChart3 },
    { id: 'achievements', label: 'Achievements', icon: Award }
  ]

  const userTypeOptions = [
    {
      id: 'dpo',
      label: 'Data Protection Officer (DPO)',
      description: 'Responsible for data privacy compliance and protection',
      icon: Shield,
      color: 'bg-blue-100 text-blue-700'
    },
    {
      id: 'it',
      label: 'IT Staff/Administrator',
      description: 'Manages IT infrastructure and cybersecurity',
      icon: Monitor,
      color: 'bg-green-100 text-green-700'
    },
    {
      id: 'admin',
      label: 'Administrative Staff',
      description: 'General administrative and clerical duties',
      icon: User,
      color: 'bg-purple-100 text-purple-700'
    }
  ]

  const securitySettings = [
    {
      id: 'password',
      title: 'Password',
      description: 'Change your account password',
      icon: Lock,
      action: 'Change Password',
      status: 'Strong'
    },
    {
      id: 'twoFactor',
      title: 'Two-Factor Authentication',
      description: 'Add an extra layer of security to your account',
      icon: Smartphone,
      action: 'Enable 2FA',
      status: 'Disabled'
    },
    {
      id: 'sessions',
      title: 'Active Sessions',
      description: 'Manage your active login sessions',
      icon: Globe,
      action: 'View Sessions',
      status: '2 Active'
    },
    {
      id: 'backup',
      title: 'Account Backup',
      description: 'Download your account data and settings',
      icon: Download,
      action: 'Download Data',
      status: 'Available'
    }
  ]

  const achievements = [
    {
      id: 'first-assessment',
      title: 'First Assessment',
      description: 'Completed your first cybersecurity assessment',
      icon: Target,
      earned: true,
      date: '2024-01-15'
    },
    {
      id: 'security-champion',
      title: 'Security Champion',
      description: 'Achieved a high security score (80%+)',
      icon: Award,
      earned: (assessmentState?.score || 0) >= 80,
      date: (assessmentState?.score || 0) >= 80 ? '2024-01-20' : null
    },
    {
      id: 'learning-enthusiast',
      title: 'Learning Enthusiast',
      description: 'Completed 5 learning modules',
      icon: FileText,
      earned: false,
      date: null
    },
    {
      id: 'incident-reporter',
      title: 'Incident Reporter',
      description: 'Reported your first security incident',
      icon: AlertTriangle,
      earned: false,
      date: null
    },
    {
      id: 'policy-expert',
      title: 'Policy Expert',
      description: 'Downloaded 10 policy templates',
      icon: FileText,
      earned: false,
      date: null
    },
    {
      id: 'consistent-user',
      title: 'Consistent User',
      description: 'Used the app for 30 consecutive days',
      icon: Calendar,
      earned: false,
      date: null
    }
  ]

  const recentActivity = [
    {
      id: 1,
      type: 'assessment',
      title: 'Completed Cybersecurity Assessment',
      description: 'Scored 75% - Medium Risk Level',
      timestamp: '2024-01-20 14:30',
      icon: Target,
      color: 'text-blue-600'
    },
    {
      id: 2,
      type: 'learning',
      title: 'Watched "Phishing Prevention" Video',
      description: 'Completed 12-minute training module',
      timestamp: '2024-01-19 10:15',
      icon: FileText,
      color: 'text-green-600'
    },
    {
      id: 3,
      type: 'download',
      title: 'Downloaded Data Privacy Policy Template',
      description: 'Policy template for government agencies',
      timestamp: '2024-01-18 16:45',
      icon: Download,
      color: 'text-purple-600'
    },
    {
      id: 4,
      type: 'profile',
      title: 'Updated Profile Information',
      description: 'Changed department and contact details',
      timestamp: '2024-01-17 09:20',
      icon: User,
      color: 'text-gray-600'
    }
  ]

  const handleEditToggle = () => {
    if (isEditing) {
      // Save changes
      userDispatch({
        type: 'UPDATE_PROFILE',
        payload: editForm
      })
    }
    setIsEditing(!isEditing)
  }

  const handleInputChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleNotificationChange = (setting, value) => {
    setNotifications(prev => ({
      ...prev,
      [setting]: value
    }))
  }

  const handlePrivacyChange = (setting, value) => {
    setPrivacy(prev => ({
      ...prev,
      [setting]: value
    }))
  }

  const handleUserTypeChange = (newType) => {
    userDispatch({
      type: 'SET_USER_TYPE',
      payload: newType
    })
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gov-50 to-cyber-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="w-20 h-20 bg-cyber-600 rounded-full flex items-center justify-center">
                <User className="w-10 h-10 text-white" />
              </div>
              <button className="absolute bottom-0 right-0 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-lg border border-gov-200">
                <Camera className="w-3 h-3 text-gov-600" />
              </button>
            </div>
          </div>
          <h1 className="text-3xl font-display font-bold text-gov-800 mb-2">
            {profile?.name || (user?.user_metadata?.name ? user.user_metadata.name : null) || user?.email?.split('@')[0] || 'User'}
          </h1>
          <p className="text-lg text-gov-600">
            {getUserTypeLabel(profile?.user_type) || (profile?.user_type ? profile.user_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Government Employee')}
          </p>
          <p className="text-gov-500">
            {profile?.department || 'Department'}, {profile?.agency || 'Agency'}
          </p>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-wrap gap-2 justify-center">
            {tabs.map((tab) => {
              const TabIcon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors duration-200 ${
                    activeTab === tab.id
                      ? 'bg-cyber-600 text-white'
                      : 'bg-white text-gov-600 hover:bg-cyber-50'
                  }`}
                >
                  <TabIcon size={16} />
                  <span>{tab.label}</span>
                </button>
              )
            })}
          </div>
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {activeTab === 'profile' && (
              <div className="space-y-6">
                {/* Profile Information */}
                <div className="card">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gov-800">Profile Information</h2>
                    {canEdit() && (
                      <button
                        onClick={handleEditToggle}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors duration-200 ${
                          isEditing ? 'btn-primary' : 'btn-secondary'
                        }`}
                      >
                        {isEditing ? (
                          <>
                            <Save size={16} />
                            <span>Save Changes</span>
                          </>
                        ) : (
                          <>
                            <Edit3 size={16} />
                            <span>Edit Profile</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        First Name
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.firstName}
                          onChange={(e) => handleInputChange('firstName', e.target.value)}
                          className="input-field"
                        />
                      ) : (
                        <p className="text-gov-800 py-2">{profile?.name?.split(' ')[0] || 'Not set'}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Last Name
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.lastName}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                          className="input-field"
                        />
                      ) : (
                        <p className="text-gov-800 py-2">{profile?.name?.split(' ').slice(1).join(' ') || 'Not set'}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Email Address
                      </label>
                      {isEditing ? (
                        <input
                          type="email"
                          value={editForm.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          className="input-field"
                        />
                      ) : (
                        <p className="text-gov-800 py-2">{user?.email || 'Not set'}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Phone Number
                      </label>
                      {isEditing ? (
                        <input
                          type="tel"
                          value={editForm.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          className="input-field"
                        />
                      ) : (
                        <p className="text-gov-800 py-2">{profile?.phone || 'Not set'}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Department
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.department}
                          onChange={(e) => handleInputChange('department', e.target.value)}
                          className="input-field"
                        />
                      ) : (
                        <p className="text-gov-800 py-2">{profile?.department || 'Not set'}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Agency/Organization
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.agency}
                          onChange={(e) => handleInputChange('agency', e.target.value)}
                          className="input-field"
                        />
                      ) : (
                        <p className="text-gov-800 py-2">{profile?.agency || 'Not set'}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Position/Title
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.position}
                          onChange={(e) => handleInputChange('position', e.target.value)}
                          className="input-field"
                        />
                      ) : (
                        <p className="text-gov-800 py-2">{profile?.position || 'Not set'}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Location
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.location}
                          onChange={(e) => handleInputChange('location', e.target.value)}
                          className="input-field"
                        />
                      ) : (
                        <p className="text-gov-800 py-2">{profile?.location || 'Not set'}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* User Type Selection */}
                <div className="card">
                  <h2 className="text-xl font-semibold text-gov-800 mb-6">User Type</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {userTypeOptions.map((type) => {
                      const TypeIcon = type.icon
                      return (
                        <div
                          key={type.id}
                          onClick={() => handleUserTypeChange(type.id)}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                            profile?.user_type === type.id
                              ? 'border-cyber-500 bg-cyber-50'
                              : 'border-gov-200 hover:border-cyber-300'
                          }`}
                        >
                          <div className="flex items-start space-x-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${type.color}`}>
                              <TypeIcon size={20} />
                            </div>
                            <div className="flex-1">
                              <h3 className="font-medium text-gov-800">{type.label}</h3>
                              <p className="text-sm text-gov-600 mt-1">{type.description}</p>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'security' && (
              <div className="space-y-6">
                <div className="card">
                  <h2 className="text-xl font-semibold text-gov-800 mb-6">Security Settings</h2>
                  <div className="space-y-4">
                    {securitySettings.map((setting) => {
                      const SettingIcon = setting.icon
                      return (
                        <div key={setting.id} className="flex items-center justify-between p-4 bg-gov-50 rounded-lg">
                          <div className="flex items-center space-x-4">
                            <div className="w-10 h-10 bg-cyber-100 rounded-lg flex items-center justify-center">
                              <SettingIcon className="w-5 h-5 text-cyber-600" />
                            </div>
                            <div>
                              <h3 className="font-medium text-gov-800">{setting.title}</h3>
                              <p className="text-sm text-gov-600">{setting.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-4">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              setting.status === 'Strong' || setting.status === 'Available' 
                                ? 'bg-success-100 text-success-700'
                                : setting.status === 'Disabled'
                                ? 'bg-warning-100 text-warning-700'
                                : 'bg-gov-100 text-gov-700'
                            }`}>
                              {setting.status}
                            </span>
                            <button className="btn-secondary text-sm">
                              {setting.action}
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <div className="card">
                  <h2 className="text-xl font-semibold text-gov-800 mb-6">Notification Preferences</h2>
                  <div className="space-y-4">
                    {Object.entries(notifications).map(([key, value]) => {
                      const labels = {
                        emailAlerts: 'Email Alerts',
                        smsAlerts: 'SMS Alerts',
                        pushNotifications: 'Push Notifications',
                        weeklyReports: 'Weekly Security Reports',
                        securityUpdates: 'Security Updates',
                        trainingReminders: 'Training Reminders',
                        incidentAlerts: 'Incident Alerts'
                      }
                      
                      const descriptions = {
                        emailAlerts: 'Receive important security alerts via email',
                        smsAlerts: 'Get critical alerts via SMS',
                        pushNotifications: 'Browser and mobile push notifications',
                        weeklyReports: 'Weekly summary of your security status',
                        securityUpdates: 'Latest cybersecurity news and updates',
                        trainingReminders: 'Reminders for training and assessments',
                        incidentAlerts: 'Immediate alerts for security incidents'
                      }
                      
                      return (
                        <div key={key} className="flex items-center justify-between p-4 bg-gov-50 rounded-lg">
                          <div>
                            <h3 className="font-medium text-gov-800">{labels[key]}</h3>
                            <p className="text-sm text-gov-600">{descriptions[key]}</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={value}
                              onChange={(e) => handleNotificationChange(key, e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyber-600"></div>
                          </label>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'privacy' && (
              <div className="space-y-6">
                <div className="card">
                  <h2 className="text-xl font-semibold text-gov-800 mb-6">Privacy Settings</h2>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Profile Visibility
                      </label>
                      <select
                        value={privacy.profileVisibility}
                        onChange={(e) => handlePrivacyChange('profileVisibility', e.target.value)}
                        className="input-field"
                      >
                        <option value="public">Public - Visible to all users</option>
                        <option value="agency">Agency Only - Visible to agency members</option>
                        <option value="private">Private - Only visible to you</option>
                      </select>
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-gov-50 rounded-lg">
                      <div>
                        <h3 className="font-medium text-gov-800">Share Progress</h3>
                        <p className="text-sm text-gov-600">Allow others in your agency to see your assessment progress</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={privacy.shareProgress}
                          onChange={(e) => handlePrivacyChange('shareProgress', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyber-600"></div>
                      </label>
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-gov-50 rounded-lg">
                      <div>
                        <h3 className="font-medium text-gov-800">Analytics</h3>
                        <p className="text-sm text-gov-600">Help improve the app by sharing anonymous usage data</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={privacy.allowAnalytics}
                          onChange={(e) => handlePrivacyChange('allowAnalytics', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyber-600"></div>
                      </label>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gov-700 mb-2">
                        Data Retention
                      </label>
                      <select
                        value={privacy.dataRetention}
                        onChange={(e) => handlePrivacyChange('dataRetention', e.target.value)}
                        className="input-field"
                      >
                        <option value="1year">1 Year</option>
                        <option value="2years">2 Years</option>
                        <option value="5years">5 Years</option>
                        <option value="indefinite">Indefinite</option>
                      </select>
                      <p className="text-sm text-gov-500 mt-1">
                        How long to keep your assessment data and activity history
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="space-y-6">
                <div className="card">
                  <h2 className="text-xl font-semibold text-gov-800 mb-6">Recent Activity</h2>
                  <div className="space-y-4">
                    {recentActivity.map((activity) => {
                      const ActivityIcon = activity.icon
                      return (
                        <div key={activity.id} className="flex items-start space-x-4 p-4 bg-gov-50 rounded-lg">
                          <div className={`w-10 h-10 bg-white rounded-lg flex items-center justify-center ${activity.color}`}>
                            <ActivityIcon size={20} />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-gov-800">{activity.title}</h3>
                            <p className="text-sm text-gov-600 mb-1">{activity.description}</p>
                            <div className="flex items-center space-x-1 text-xs text-gov-500">
                              <Clock size={12} />
                              <span>{activity.timestamp}</span>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'achievements' && (
              <div className="space-y-6">
                <div className="card">
                  <h2 className="text-xl font-semibold text-gov-800 mb-6">Achievements</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {achievements.map((achievement) => {
                      const AchievementIcon = achievement.icon
                      return (
                        <div
                          key={achievement.id}
                          className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                            achievement.earned
                              ? 'border-success-300 bg-success-50'
                              : 'border-gov-200 bg-gov-50 opacity-60'
                          }`}
                        >
                          <div className="flex items-start space-x-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              achievement.earned
                                ? 'bg-success-100 text-success-600'
                                : 'bg-gov-200 text-gov-500'
                            }`}>
                              <AchievementIcon size={20} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <h3 className={`font-medium ${
                                  achievement.earned ? 'text-success-800' : 'text-gov-600'
                                }`}>
                                  {achievement.title}
                                </h3>
                                {achievement.earned && (
                                  <CheckCircle className="w-4 h-4 text-success-600" />
                                )}
                              </div>
                              <p className={`text-sm ${
                                achievement.earned ? 'text-success-700' : 'text-gov-500'
                              }`}>
                                {achievement.description}
                              </p>
                              {achievement.earned && achievement.date && (
                                <div className="flex items-center space-x-1 mt-2 text-xs text-success-600">
                                  <Calendar size={12} />
                                  <span>Earned on {new Date(achievement.date).toLocaleDateString()}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}

export default Profile